import { InformationalBanner } from '@wonderschool/common-base-ui';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Checkbox } from 'semantic-ui-react'; // eslint-disable-line no-restricted-imports

import { calculateTotalFromInvoiceItems } from '../../../helpers/invoices';
import { useInvoiceEditDetails, useInvoiceEditItems, useInvoiceEditStudents } from '../../../hooks/useInvoiceEdit';

// Import components
import {
  calculateCadenceTransitionTotal,
  getIntervalTransitionServicePeriod,
  getInvoicePlanStatus,
  getUpdatedBillingDates,
  isCadenceTransitionConfirmation,
  validateCadenceTransitionFinancials,
} from '../../../helpers/invoicePlans';
import { resolvePlanLocation } from '../../../hooks/useLocations';
import { StudentsListWidget } from './Widgets';

import InvoicePlanDetailsContainer from './InvoicePlans/InvoicePlanDetailsContainer';
import { StepsContainer } from './Steps';

const selectLocations = createSelector([(state) => state.locations], (locations) => locations?.list ?? []);
const selectOrganizationStudents = createSelector([(state) => state.students], (students) => students?.list ?? []);
const selectRooms = createSelector([(state) => state.rooms], (rooms) => rooms?.list ?? []);

export default function StepConfirmAndSave({ isInvoice = true, onSave, isSaving = false }) {
  const { t } = useTranslation();
  const details = useInvoiceEditDetails();
  const locations = useSelector(selectLocations);
  const organizationStudents = useSelector(selectOrganizationStudents);
  const rooms = useSelector(selectRooms);
  const invoiceItems = useInvoiceEditItems();
  const students = useInvoiceEditStudents();
  const planLocation = resolvePlanLocation(locations, details?.location, students[0], organizationStudents, rooms);
  const isIntervalTransition = isCadenceTransitionConfirmation(details);
  const financialError = useMemo(
    () => (isIntervalTransition ? validateCadenceTransitionFinancials(invoiceItems) : null),
    [invoiceItems, isIntervalTransition]
  );
  const hasAuthoritativeTimezone = !!planLocation?.timezone && !!moment.tz.zone(planLocation.timezone);
  const timezone = hasAuthoritativeTimezone ? planLocation.timezone : isIntervalTransition ? null : 'utc';
  const confirmationDetails = useMemo(() => {
    const materialized = isIntervalTransition ? { ...details, location: planLocation?.id } : details;
    const recovery = materialized?.intervalTransitionRecovery;
    if (!recovery?.schedulerSnapshot?.dateServiceEnd || !timezone) return materialized;
    const servicePeriod = getIntervalTransitionServicePeriod({
      dateServiceEnd: recovery.schedulerSnapshot.dateServiceEnd,
      dateDue: recovery.effectiveDue,
      dateStart: materialized.dateStart,
      billingInterval: recovery.nextBillingInterval,
      timezone,
    });
    return { ...materialized, intervalChangeServicePeriodPreview: servicePeriod.displayInterval };
  }, [details, isIntervalTransition, planLocation?.id, timezone]);
  const [alreadyPaid, setAlreadyPaid] = useState(false);

  const loggedInUser = useSelector((state) => state.user);

  const isEdit = !!details?.id;

  const dataToSave = useMemo(() => {
    if (financialError) return null;
    return createDataToSave(isInvoice, confirmationDetails, invoiceItems, students, loggedInUser, timezone, isEdit);
  }, [financialError, isInvoice, confirmationDetails, invoiceItems, students, loggedInUser, timezone, isEdit]);
  const financialErrorMessage = financialError
    ? t(
        financialError.type === 'percentage'
          ? 'billing.tuitionAutofill.invalidPercentageRow'
          : 'billing.tuitionAutofill.invalidMoneyRow',
        {
          row:
            financialError.row.item ||
            financialError.row.description ||
            t('billing.invoicePlanReview.recurringChargeFallback'),
        }
      )
    : null;

  return (
    <>
      <StepsContainer
        isInvoice={isInvoice}
        isEdit={isEdit}
        CurrentForm={ConfirmForm}
        Widgets={Widgets}
        onDone={onSaveLocal}
        isSaving={isSaving}
        disableNext={Boolean(isIntervalTransition && (financialError || !hasAuthoritativeTimezone))}
      />
    </>
  );

  function ConfirmForm() {
    return (
      <>
        {!isInvoice && (
          <>
            {isIntervalTransition && !hasAuthoritativeTimezone && (
              <InformationalBanner
                type="error"
                data-testid="interval-location-blocked"
                label={t('billing.intervalChange.locationUnavailable')}
              />
            )}
            {financialErrorMessage && (
              <InformationalBanner
                type="error"
                data-testid="interval-financial-blocked"
                label={financialErrorMessage}
              />
            )}
            {details?.intervalTransitionRecovery && (
              <InformationalBanner
                type="info"
                data-testid="interval-recovery-confirmation"
                label={t('billing.intervalChange.recoveryConfirmation', {
                  defaultValue:
                    'Retry attempt {{attemptId}} immediately: {{previous}} → {{next}}, effective {{effectiveDue}}. Existing invoices remain unchanged.',
                  attemptId: details.intervalTransitionRecovery.attemptId,
                  previous: t(details.intervalTransitionRecovery.previousBillingInterval),
                  next: t(details.intervalTransitionRecovery.nextBillingInterval),
                  effectiveDue: timezone
                    ? moment(details.intervalTransitionRecovery.effectiveDue).tz(timezone).format('L')
                    : '',
                })}
              />
            )}
            {dataToSave && (!isIntervalTransition || hasAuthoritativeTimezone) && (
              <InvoicePlanDetailsContainer showActionButtons={false} showProjectedCharges invoicePlan={dataToSave} />
            )}
          </>
        )}
      </>
    );
  }
  function Widgets() {
    return (
      <>
        {isInvoice && (
          <Checkbox
            onChange={onChangeAlreadyPaid}
            name={'alreadyPaid'}
            checked={alreadyPaid}
            label={t('billing.invoiceCreation.confirm.markPaidLabel')}
          />
        )}
        {<StudentsListWidget students={students} onRemove={onRemoveStudent} allowDelete={false} />}
      </>
    );
  }
  function onRemoveStudent(_student) {
    // TODO, will use in future feature
    //  setSelectedStudents (selectedStudents.filter (s => s.id !== student.id));
  }
  function onChangeAlreadyPaid(e, { checked }) {
    setAlreadyPaid(checked);
  }
  function onSaveLocal() {
    if (isIntervalTransition && (!hasAuthoritativeTimezone || financialError)) return;
    if (onSave) onSave(dataToSave, alreadyPaid);
  }
}

function createDataToSave(isInvoice, details, invoiceItems, students, loggedInUser, timezone, isEdit) {
  if (isInvoice) {
    return {};
  } else {
    return createInvoicePlan(details, invoiceItems, students, loggedInUser, timezone, isEdit);
  }
}
function createInvoicePlan(details, invoiceItems, students, loggedInUser, timezone, isEdit) {
  return {
    ...prepareInvoicePlanDetailsForSave(details, invoiceItems, loggedInUser, timezone, isEdit),
    invoiceItemList: invoiceItems,
    students,
  };
}

function prepareInvoicePlanDetailsForSave(details, invoiceItems, loggedInUser, timezone, isEdit) {
  const loggedInUserInfo = { id: loggedInUser.uid, displayName: loggedInUser.displayName };
  const author = details?.id ? { updatedBy: loggedInUserInfo } : { createdBy: loggedInUserInfo };

  const isIntervalTransition = isCadenceTransitionConfirmation(details);
  const total = isIntervalTransition
    ? calculateCadenceTransitionTotal(invoiceItems)
    : calculateTotalFromInvoiceItems(invoiceItems);

  if (details?.intervalTransitionRecovery) {
    return {
      ...details,
      ...author,
      isInvoice: false,
      total,
    };
  }

  const updatedBillingDates = getUpdatedBillingDates({
    invoicePlan: details,
    timezone,
    isEditing: isEdit,
  });
  const status = getInvoicePlanStatus(updatedBillingDates.dateDue, updatedBillingDates.dateEnd);

  const data = {
    ...details,
    ...author,
    ...updatedBillingDates,
    isInvoice: false,
    status,
    total,
  };
  return data;
}
